import {
  getRecentlyViewedGoods,
  type GetRecentlyViewedGoodsResponse,
  type Goods,
} from 'shared/src/api';
import { queryKey } from 'shared/src/constant';
import {
  useSuspenseInfiniteQuery,
  type InfiniteData,
} from '@tanstack/react-query';
import type { GoodsData } from '@banhala/ui-kit';

export const useGetRecentlyViewedGoods = () => {
  const {
    data,
    fetchNextPage,
    hasNextPage,
    refetch,
    isRefetching,
    isFetchingNextPage,
  } = useSuspenseInfiniteQuery({
    queryKey: queryKey.getInfiniteRecentlyViewedGoods,
    queryFn: ({ pageParam }) => {
      return getRecentlyViewedGoods({ last_sno: pageParam });
    },
    initialPageParam: 0,
    select: selectFn,
    getNextPageParam: ({ data }) => {
      const lastIdx = data.goods.length - 1;

      if (data.last_sno === null || lastIdx === -1) {
        return;
      }
      const lastGoodsSno = data.goods[lastIdx].sno;
      return lastGoodsSno;
    },
    staleTime: 0,
    gcTime: 0,
  });
  return {
    recentlyViewedGoods: data,
    hasNextPage,
    refetch,
    isRefetching,
    getNextRecentlyViewedGoods: () => {
      if (!hasNextPage) return;

      if (isFetchingNextPage) return;

      fetchNextPage();
    },
  };
};

const selectFn: SelectFn = data => {
  return {
    ...data.pages[0].data,
    goods: data.pages
      .map(page =>
        page.data.goods.map(v => ({
          ...v,
          deliveryType: v.delivery_type,
          like: v.like !== null,
          thumbnail: {
            src: v.image_webp,
            freesSrc: v.image,
            closedReason: v.render.data.closed_reason ?? undefined,
            hideLike: !v.render.data.actions.includes('LIKE'),
            badges: v.render.data.image_badge_list.map(badge => ({
              uri: badge.image_url,
              width: badge.image_width,
              height: badge.image_height,
            })),
          },
          contents: {
            price: {
              price: v.render.data.price,
              discount: v.render.data.discount_rate ?? '',
            },
            name: {
              goodsName: v.name,
              marketName: v.render.data.store_text ?? v.render.data.market_name,
              nameLineCount: v.render.data.name_lines,
              showBrand: false,
            },
            topNudging: v.render.data.top_nudging_text ?? undefined,
            priceDescription: v.render.data.price_description
              ? { name: v.render.data.price_description.text }
              : undefined,
            imageAd: v.render.data.has_ad_badge,
            nudge: v.render.data.nudging_text ?? undefined,
            chips: v.render.data.chips.map(chip => {
              if (chip.meta) {
                return {
                  type: chip.type,
                  name: chip.name,
                  customTag: chip.meta
                    ? {
                        bgColor: chip.meta.bg_color,
                        borderColor: chip.meta.border_color,
                        borderWidth: chip.meta.border_width,
                        borderRadius: chip.meta.rounded_corner_radius,
                        textColor: chip.meta.text_color,
                        textType: chip.meta.text_style,
                      }
                    : undefined,
                };
              }
              return {
                type: chip.type,
                name: chip.name,
              };
            }),
          },
        })),
      )
      .flat(),
  };
};

type GoodsInList = GoodsData & Omit<Goods, 'like'> & { like: boolean };

type SelectFn = (
  data: InfiniteData<{
    statusCode: number;
    data: GetRecentlyViewedGoodsResponse;
  }>,
) => Omit<GetRecentlyViewedGoodsResponse, 'goods'> & {
  goods: Array<GoodsInList>;
};
